import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X, Image as ImageIcon, Video } from 'lucide-react';

// Sortable Item Component
const SortableItem = ({ id, url, type, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 ${
        isDragging ? 'border-[#003456] shadow-lg' : 'border-transparent'
      }`}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-1 left-1 p-1 bg-black/50 text-white rounded cursor-grab active:cursor-grabbing z-10 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <GripVertical className="h-4 w-4" />
      </div>

      {/* Image/Video Preview */}
      {type === 'video' ? (
        <div className="w-full h-full flex items-center justify-center bg-gray-800">
          <Video className="h-8 w-8 text-white" />
        </div>
      ) : (
        <img 
          src={url} 
          alt="" 
          className="w-full h-full object-cover"
          draggable={false}
        />
      )}

      {/* Remove Button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(id);
        }}
        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10 hover:bg-red-600"
      >
        <X className="h-3 w-3" />
      </button>

      {/* Order Indicator */}
      <div className="absolute bottom-1 left-1 px-2 py-0.5 bg-black/60 text-white text-xs rounded">
        #{id.split('-').pop()}
      </div>
    </div>
  );
};

// Main Sortable Gallery Component
export const SortableGallery = ({ images, onReorder, onRemove, className = '' }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Generate unique IDs for each image
  const items = images.map((img, idx) => ({
    id: `gallery-${idx}`,
    url: typeof img === 'string' ? img : img.url,
    type: typeof img === 'string' ? 'image' : (img.type || 'image'),
    originalIndex: idx,
  }));

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      const newOrder = arrayMove(images, oldIndex, newIndex);
      onReorder(newOrder);
    }
  };

  const handleRemove = (id) => {
    const index = items.findIndex((item) => item.id === id);
    if (index !== -1) {
      onRemove(index);
    }
  };

  if (!images || images.length === 0) {
    return (
      <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
        <ImageIcon className="h-10 w-10 mx-auto mb-2 text-gray-300" />
        <p className="text-gray-500">No images added yet</p>
        <p className="text-xs text-gray-400 mt-1">Add images using the button above</p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items.map((i) => i.id)} strategy={rectSortingStrategy}>
        <div className={`grid grid-cols-4 gap-3 ${className}`}>
          {items.map((item) => (
            <SortableItem
              key={item.id}
              id={item.id}
              url={item.url}
              type={item.type}
              onRemove={handleRemove}
            />
          ))}
        </div>
      </SortableContext>
      <p className="text-xs text-gray-500 mt-2">
        Drag and drop to reorder images. The first image will be shown as the primary.
      </p>
    </DndContext>
  );
};

export default SortableGallery;
